import { ChangeEvent, FormEvent, ReactNode, useEffect, useState } from "react";
import { ListControls, usePagedList } from "./Pagination";
import { AuditPage } from "./AuditPage";
import { SettingsPage } from "./SettingsPage";
import { CompaniesPage, PlansPage } from "./CompaniesPage";
import { TimelinePage } from "./TimelinePage";
import { DevicesPage } from "./DevicesPage";
import { ActivityPage } from "./ActivityPage";
import { can, isScopedSession } from "./can";
import { FilterBar, Metric, PageHeader } from "./ui";
import { bytes, duration, lifecycleLabel, localInput, percent } from "./format";
import { TelemetryChart } from "./TelemetryChart";
import {
  api,

  CompanyAccess,
  Device,
  DeviceDetail,
  DeviceEvent,
  DeviceIncident,
  DeviceInventory,
  EventImport,
  EventReportSummary,
  ManagedUser,
  Partner,
  Plan,
  Policy,
  session,
  Tenant,
  TenantEvent,
  UserCompanyRole,
} from "./api";

// Cabeçalho comum de página: título/subtítulo à esquerda, ações à direita.
// Padroniza a hierarquia (título → ação), antes duplicada a cada tela.
// O badge antigo só sabia se o processo existia. Com o diagnóstico da 0.16.0 dá para
// diferenciar sessão remota, política não lida, captura desligada e falha de envio.
function companionBadge(d: Device): { label: string; tone: "online" | "offline" | "warning"; title: string } | null {
  if (typeof d.sessionCompanionRunning !== "boolean") return null;
  if (!d.sessionCompanionRunning) {
    return {
      label: "Companion ausente",
      tone: "offline",
      title: "O serviço não encontrou o Guardian.Session em uma sessão interativa. Sem o companion não há captura de tela (o serviço roda na sessão 0).",
    };
  }
  const c = d.companion;
  if (!c) {
    return {
      label: "Companion ativo",
      tone: "online",
      title: "Agente sem diagnóstico detalhado (anterior à 0.16.0). Atualize o agente para ver política e último envio.",
    };
  }
  const details = [
    c.sessionId != null ? `sessão ${c.sessionId}` : null,
    c.policyRevision != null ? `política r${c.policyRevision}` : null,
    c.statusAt ? `status ${new Date(c.statusAt).toLocaleTimeString("pt-BR")}` : null,
    c.lastSendOkAt ? `último envio ${new Date(c.lastSendOkAt).toLocaleTimeString("pt-BR")}` : "nenhum envio registrado",
    c.lastError ? `erro: ${c.lastError}` : null,
  ].filter(Boolean).join(" · ");
  if (c.terminalServerSession)
    return { label: "Companion: sessão remota", tone: "warning", title: `Sessão RDP: a captura é ignorada por design. ${details}` };
  // statusAt vem do relógio da máquina do agente. Corrige pelo drift medido no heartbeat
  // (servidor - agente) para não acusar "sem sinal" em PC com relógio atrasado/adiantado.
  const driftMs = (d.clockDriftSeconds ?? 0) * 1000;
  const statusAge = c.statusAt ? Date.now() - new Date(c.statusAt).getTime() - driftMs : Number.POSITIVE_INFINITY;
  if (statusAge > 120_000)
    return { label: "Companion sem sinal", tone: "warning", title: `Sem status recente do companion. ${details}` };
  if (!c.policyLoaded)
    return { label: "Companion sem política", tone: "warning", title: `O companion não conseguiu ler o policy.json. ${details}` };
  if (!c.screenshotsEnabled)
    return { label: "Companion: captura desligada", tone: "warning", title: `A política aplicada está com a captura desabilitada. ${details}` };
  if (c.lastError)
    return { label: "Companion com erro", tone: "warning", title: `Última falha: ${c.lastError}. ${details}` };
  return { label: "Companion ativo", tone: "online", title: details };
}
// O token trocado para entrar numa empresa recebe a claim `scoped_tenant` e perde
// acesso a /platform. Usamos a claim (com fallback para o resumo salvo pela API) em
// vez do papel, que não distingue o Suporte da plataforma do Suporte em escopo.
// A API emite as capacidades no login (e em /auth/me). Menu e botões leem daqui em
// vez de comparar papel: papel não distingue, por exemplo, Suporte da plataforma de
// Suporte em escopo.


const roleLabel = (role?: string) =>
  ({
    owner: "ADM da empresa",
    admin: "Administrador",
    support: "Suporte",
    viewer: "User",
    platform_admin: "Master",
    platform_support: "Suporte da plataforma",
    partner_admin: "Revenda — ADM",
    partner_support: "Revenda — Suporte",
  })[role ?? ""] ?? "";
type Theme = "dark" | "light";
const readTheme = (): Theme => localStorage.getItem("dbguardian.theme") === "light" ? "light" : "dark";
const applyTheme = (theme: Theme) => { document.documentElement.dataset.theme = theme; localStorage.setItem("dbguardian.theme", theme); };

export function App() {
  const [route, setRoute] = useState(
    window.location.hash || (session.token() ? "#/" : "#/login"),
  );
  // Sessões abertas antes da 0.29.0 não têm capacidades: o token também não tem a claim
  // "cap", então além do resumo é preciso reemitir o token (refresh) — só reidratar o
  // usuário deixaria o menu certo e as chamadas à API sem permissão.
  const [hydrated, setHydrated] = useState(() => !session.token() || Boolean(session.user()?.capabilities));
  useEffect(() => {
    const token = session.token();
    if (hydrated || !token) return;
    api
      .refresh()
      .then((result) => session.save(result.accessToken, result.user))
      .catch(() => session.clear())
      .finally(() => setHydrated(true));
  }, [hydrated]);
  useEffect(() => {
    applyTheme(readTheme());
    const change = () => setRoute(window.location.hash || "#/");
    addEventListener("hashchange", change);
    return () => removeEventListener("hashchange", change);
  }, []);
  if (!session.token() || route === "#/login")
    return <Login onSignedIn={() => setRoute("#/")} />;
  // Perfis privilegiados sem segundo fator recebem um token limitado: só dá para
  // cadastrar o MFA (e chamar /auth/me) até confirmar o código.
  if (session.user()?.mfaEnrollmentRequired) return <MfaEnrollment />;
  if (!hydrated) return <p className="muted">Carregando…</p>;
  const deviceId = route.match(/^#\/devices\/([a-f0-9-]+)$/i)?.[1];
  const timelineId = route.match(/^#\/devices\/([a-f0-9-]+)\/timeline(?:\?.*)?$/i)?.[1];
  const page = timelineId ? (
    <TimelinePage deviceId={timelineId} />
  ) : deviceId ? (
    <DevicePage id={deviceId} />
  ) : route === "#/users" ? (
    <UsersPage />
  ) : route === "#/audit" ? (
    <AuditPage />
  ) : route === "#/events" ? (
    <EventsPage />
  ) : route === "#/reports" ? (
    <ReportsPage />
  ) : route === "#/policies" ? (
    <PoliciesPage />
  ) : route === "#/security" ? (
    <SecurityPage />
  ) : route === "#/companies" ? (
    <CompaniesPage />
  ) : route === "#/plans" ? (
    <PlansPage />
  ) : route === "#/settings" ? (
    <SettingsPage />
  ) : route === "#/activity" ? (
    <ActivityPage />
  ) : (
    <DevicesPage />
  );
  return <Shell route={route}>{page}</Shell>;
}

function NavIcon({ name }: { name: string }) {
  const paths: Record<string, ReactNode> = {
    devices: <><rect x="2.5" y="4" width="19" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></>,
    activity: <path d="M3 12h4l2.5-6 3.5 12 2.5-6H21" />,
    events: <><path d="M12 4 2.7 20h18.6L12 4Z" /><path d="M12 10v4.6M12 17.3v.1" /></>,
    reports: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
    policies: <><path d="M6 3h9l4 4v14H6z" /><path d="M15 3v5h4M9 12h7M9 16h7" /></>,
    users: <><circle cx="9" cy="8" r="3.2" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><path d="M16 5.5a3.2 3.2 0 0 1 0 6M17 14.2c2.4.7 4 3 4 5.8" /></>,
    security: <><path d="M12 3l7 3v6c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6l7-3Z" /><path d="M9.4 12l1.9 1.9L15 10" /></>,
    settings: <><path d="M4 7h8M17 7h3M4 17h3M11 17h9" /><circle cx="14.5" cy="7" r="2.2" /><circle cx="9" cy="17" r="2.2" /></>,
    audit: <><path d="M8 4h11v16H8z" /><path d="M8 4H5v16h3M11 9h5M11 13h5" /></>,
    companies: <><path d="M4 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16" /><path d="M14 9h5a1 1 0 0 1 1 1v11M2 21h20" /><path d="M7 8h3M7 12h3M7 16h3M17 13h2M17 17h2" /></>,
    plans: <><path d="M12 3 3 8l9 5 9-5-9-5Z" /><path d="M3 13l9 5 9-5M3 17.5l9 5 9-5" /></>,
  };
  return (
    <svg className="nav-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name] ?? paths.devices}
    </svg>
  );
}

function Shell({
  children,
  route,
}: {
  children: React.ReactNode;
  route: string;
}) {
  const user = session.user();
  const [theme, setTheme] = useState<Theme>(readTheme());
  const [widget, setWidget] = useState<{ supportWidgetEnabled: boolean; supportWidgetHtml: string }>();
  useEffect(() => { api.uiSettings().then(setWidget).catch(() => undefined); }, []);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("guardian.sidebar") === "collapsed");
  const toggleSidebar = () => setCollapsed((value) => {
    const next = !value;
    localStorage.setItem("guardian.sidebar", next ? "collapsed" : "expanded");
    return next;
  });
  const [mobileNav, setMobileNav] = useState(false);
  useEffect(() => { setMobileNav(false); }, [route]);
  // O Master entra em outra empresa com role "support" no token de escopo; a claim
  // platform_role é o que mantém o acesso ao conteúdo e à auditoria.
  // As telas de plataforma (/platform/*) exigem token sem escopo. O escopo não se
  // deduz do papel: o Suporte da plataforma tem role "support" na empresa de origem e
  // ainda precisa de "Empresas" para entrar nas empresas com concessão.
  const scopedSession = isScopedSession(session.token(), user);
  // Multiempresa: o seletor só aparece quando há mais de uma empresa disponível.
  const [companies, setCompanies] = useState<CompanyAccess[]>([]);
  useEffect(() => { api.companies().then((page) => setCompanies(page.items)).catch(() => undefined); }, []);
  const currentCompany = companies.find((company) => company.current);
  const [switchError, setSwitchError] = useState("");
  const switchCompany = async (tenantId: string) => {
    if (!tenantId || tenantId === currentCompany?.tenantId) return;
    setSwitchError("");
    try {
      const result = await api.enterCompany(tenantId);
      session.save(result.accessToken, result.user);
      // Recarregar garante que menu, capacidades e escopo venham do token novo.
      location.reload();
    } catch (error) {
      // Sem isso, um 403 (concessão revogada, usuário desativado) sumia em silêncio
      // e o seletor ficava exibindo a empresa nova sem token correspondente.
      setSwitchError(error instanceof Error ? error.message : "Não foi possível trocar de empresa.");
      void api.companies().then((page) => setCompanies(page.items)).catch(() => undefined);
    }
  };
  const navItems = [
    // Operação: o dia a dia de quem acompanha os equipamentos.
    {
      href: "#/",
      icon: "devices",
      label: "Equipamentos",
      group: "Operação",
      show: true,
    },
    {
      href: "#/activity",
      icon: "activity",
      label: "Atividade do usuário",
      group: "Operação",
      show: can(user, "content.read"),
    },
    {
      href: "#/events",
      icon: "events",
      label: "Eventos do Windows",
      group: "Operação",
      show: can(user, "content.read"),
    },
    // Análise: visões agregadas.
    { href: "#/reports", icon: "reports", label: "Relatórios", group: "Análise", show: can(user, "content.read") },
    // Administração da empresa.
    { href: "#/policies", icon: "policies", label: "Políticas", group: "Administração", show: true },
    { href: "#/users", icon: "users", label: "Usuários", group: "Administração", show: can(user, "user.manage") },
    { href: "#/security", icon: "security", label: "Segurança", group: "Administração", show: true },
    // Plataforma: só quem administra empresas/planos.
    {
      href: "#/companies",
      icon: "companies",
      // Revenda entra pela carteira; quem administra empresas vê "Empresas".
      label: can(user, "tenant.manage") ? "Empresas" : "Carteira",
      group: "Plataforma",
      show: (can(user, "tenant.manage") || can(user, "portfolio.read")) && !scopedSession,
    },
    { href: "#/plans", icon: "plans", label: "Planos e contratos", group: "Plataforma", show: can(user, "plan.manage") && !scopedSession },
    { href: "#/settings", icon: "settings", label: "Configurações", group: "Plataforma", show: true },
    // Governança.
    { href: "#/audit", icon: "audit", label: "Auditoria", group: "Governança", show: can(user, "audit.read") },
  ];
  const navSections = ["Operação", "Análise", "Administração", "Plataforma", "Governança"]
    .map((label) => ({ label, items: navItems.filter((item) => item.show && item.group === label) }))
    .filter((section) => section.items.length > 0);
  const isActive = (href: string) => route === href || (href === "#/" && route.startsWith("#/devices"));
  const toggleTheme = () => { const next = theme === "dark" ? "light" : "dark"; setTheme(next); applyTheme(next); };
  return (
    <div className={"app-shell" + (collapsed ? " sidebar-collapsed" : "")}>
      <div className="topbar">
        <button className="menu-button" onClick={() => setMobileNav(true)} aria-label="Abrir menu" aria-expanded={mobileNav} aria-controls="mobile-nav">☰</button>
        <span className="topbar-brand">Guardian</span>
        <div className="topbar-company">
          {companies.length > 1 ? (
            <select
              value={currentCompany?.tenantId ?? ""}
              onChange={(event) => void switchCompany(event.target.value)}
              aria-label="Empresa"
            >
              {companies.map((company) => (
                <option key={company.tenantId} value={company.tenantId}>
                  {company.tenantName || company.slug || "Empresa atual"}
                </option>
              ))}
            </select>
          ) : (
            <span className="topbar-company-name">
              {currentCompany?.tenantName || currentCompany?.slug || user?.displayName || ""}
            </span>
          )}
        </div>
        {switchError && <small className="company-switch-error" role="alert">{switchError}</small>}
        <span className="topbar-spacer" />
        <span className="topbar-user">{user?.displayName}</span>
        <button
          className="topbar-logout"
          onClick={() => {
            session.clear();
            location.hash = "#/login";
          }}
        >
          Sair
        </button>
      </div>
      <aside className={"sidebar" + (collapsed ? " collapsed" : "")}>
        <div className="brand-box">
          <img
            className="brand-logo"
            src="/brand/dbarros-horizontal.png"
            alt="Guardian Insight"
          />
          <strong>Guardian</strong>
          <span className="brand-mark" aria-hidden="true">G</span>
        </div>
        <nav>
          {navSections.map((section) => (
            <div className="nav-section" key={section.label}>
              <span className="nav-section-label">{section.label}</span>
              {section.items.map((item) => (
                <a key={item.href} href={item.href} className={isActive(item.href) ? "active" : ""} title={item.label}>
                  <span className="nav-icon"><NavIcon name={item.icon} /></span>
                  <span className="nav-label">{item.label}</span>
                </a>
              ))}
            </div>
          ))}
        </nav>
        <div className="account">
          {companies.length > 1 && (
            <select
              className="company-switcher"
              value={currentCompany?.tenantId ?? ""}
              onChange={(event) => void switchCompany(event.target.value)}
              title="Empresa"
              aria-label="Empresa"
            >
              {companies.map((company) => (
                <option key={company.tenantId} value={company.tenantId}>
                  {company.tenantName || company.slug || "Empresa atual"}
                </option>
              ))}
            </select>
          )}
          {switchError && <small className="company-switch-error" role="alert">{switchError}</small>}
          <span>{user?.displayName}</span>
          <small>{user?.platformRole === "platform_admin" ? "Master" : roleLabel(user?.role)}</small>
          {user?.platformRole && <small>Acesso Guardian Insight</small>}
        </div>
        <button
          className="sidebar-toggle"
          onClick={toggleSidebar}
          title={collapsed ? "Expandir menu" : "Recolher menu"}
          aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
        >
          <span className="nav-icon" aria-hidden="true">{collapsed ? "»" : "«"}</span>
          <span className="nav-label">{collapsed ? "Expandir" : "Recolher"}</span>
        </button>
        <button
          className="logout"
          onClick={() => {
            session.clear();
            location.hash = "#/login";
          }}
        >
          <span className="nav-icon" aria-hidden="true">⎋</span>
          <span className="nav-label">Sair</span>
        </button>
      </aside>
      {mobileNav && <div className="nav-backdrop" onClick={() => setMobileNav(false)} />}
      <nav id="mobile-nav" className={"mobile-nav" + (mobileNav ? " open" : "")} aria-hidden={!mobileNav}>
        <div className="mobile-nav-head">
          <strong>Guardian</strong>
          <button className="nav-close" onClick={() => setMobileNav(false)} aria-label="Fechar menu">×</button>
        </div>
        <div className="mobile-nav-list">
          {navSections.map((section) => (
            <div className="mobile-nav-section" key={section.label}>
              <span className="nav-section-label">{section.label}</span>
              {section.items.map((item) => (
                <a key={item.href} href={item.href} className={isActive(item.href) ? "active" : ""} onClick={() => setMobileNav(false)}>
                  <span className="nav-icon"><NavIcon name={item.icon} /></span>
                  <span className="nav-label">{item.label}</span>
                </a>
              ))}
            </div>
          ))}
        </div>
        <div className="mobile-nav-foot">
          {companies.length > 1 && (
            <select
              className="company-switcher"
              value={currentCompany?.tenantId ?? ""}
              onChange={(event) => void switchCompany(event.target.value)}
              aria-label="Empresa"
            >
              {companies.map((company) => (
                <option key={company.tenantId} value={company.tenantId}>
                  {company.tenantName || company.slug || "Empresa atual"}
                </option>
              ))}
            </select>
          )}
          {switchError && <small className="company-switch-error" role="alert">{switchError}</small>}
          <span>{user?.displayName}</span>
          <button className="topbar-logout" onClick={() => { session.clear(); location.hash = "#/login"; }}>Sair</button>
        </div>
      </nav>
      <main><div className="global-toolbar"><button className="theme-toggle" onClick={toggleTheme}>{theme === "dark" ? "☀ Tema claro" : "☾ Tema escuro"}</button></div>{children}</main>
      {widget?.supportWidgetEnabled && widget.supportWidgetHtml && <iframe className="support-widget" title="Suporte" srcDoc={widget.supportWidgetHtml} sandbox="allow-scripts allow-forms allow-popups" referrerPolicy="no-referrer" />}
    </div>
  );
}

function Login({ onSignedIn }: { onSignedIn?: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api.login(email, password, totp, remember);
      session.save(result.accessToken, result.user, remember);
      location.hash = "#/";
      // Não depender só do hashchange: se o hash já for "#/", o evento não
      // dispara e a tela ficaria no login até um refresh.
      onSignedIn?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha no login.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="login-page">
      <section className="login-copy">
        <div className="login-logo">
          <img src="/brand/dbarros-horizontal.png" alt="Guardian Insight" />
          <strong>Guardian</strong>
        </div>
        <h1>Visibilidade real sobre cada equipamento.</h1>
        <p>
          Telemetria, políticas e evidências operacionais em uma única central
          segura.
        </p>
      </section>
      <section className="login-card">
        <form onSubmit={submit}>
          <p className="eyebrow">ACESSO SEGURO</p>
          <h2>Entrar no painel</h2>
          <label>
            E-mail
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
            />
          </label>
          <label>
            Senha
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <label>
            Código MFA <small>(se habilitado)</small>
            <input
              className="totp-input"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
              autoComplete="one-time-code"
            />
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            Manter conectado por 12 horas
          </label>
          {error && <p className="error">{error}</p>}
          <button disabled={busy}>{busy ? "Entrando…" : "Entrar"}</button>
          <small>Protegido por senha, TOTP e limitação de tentativas.</small>
        </form>
      </section>
    </div>
  );
}

function MfaEnrollment() {
  const [setup, setSetup] = useState<{ secret: string; otpAuthUri: string }>();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .setupMfa()
      .then(setSetup)
      .catch((e) =>
        setError(e instanceof Error ? e.message : "Falha ao preparar o MFA."),
      );
  }, []);
  const confirm = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.confirmMfa(code);
      const refreshed = await api.refresh();
      session.save(refreshed.accessToken, refreshed.user);
      window.location.reload();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Código inválido. Tente novamente.",
      );
      setBusy(false);
    }
  };
  return (
    <div className="login-shell">
      <section className="login-card">
        <p className="eyebrow">SEGURANÇA OBRIGATÓRIA</p>
        <h1>Ative a autenticação em duas etapas</h1>
        <p className="muted">
          Seu perfil tem acesso administrativo, então o segundo fator é
          obrigatório. Cadastre o segredo abaixo no aplicativo autenticador e
          confirme com o código de seis dígitos.
        </p>
        {error && <p className="error">{error}</p>}
        {!setup && !error && <p className="muted">Preparando o segredo…</p>}
        {setup && (
          <form onSubmit={confirm}>
            <label>
              Segredo
              <input readOnly value={setup.secret} />
            </label>
            <label>
              Código de 6 dígitos
              <input
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                autoComplete="one-time-code"
              />
            </label>
            <button disabled={busy}>
              {busy ? "Confirmando…" : "Confirmar e entrar"}
            </button>
            <small className="muted">
              Também é possível importar por: {setup.otpAuthUri}
            </small>
          </form>
        )}
        <button
          className="link-button"
          onClick={() => {
            session.clear();
            window.location.reload();
          }}
        >
          Sair
        </button>
      </section>
    </div>
  );
}

function SecurityPage() {
  const [setup, setSetup] = useState<{ secret: string; otpAuthUri: string }>();
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const prepare = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      setSetup(await api.setupMfa());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao preparar MFA.");
    } finally {
      setBusy(false);
    }
  };
  const confirm = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.confirmMfa(code);
      setSetup(undefined);
      setCode("");
      setMessage(
        "MFA habilitado. Os próximos logins exigirão o código do aplicativo.",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Código inválido.");
    } finally {
      setBusy(false);
    }
  };
  const disable = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.disableMfa(password, disableCode);
      setPassword("");
      setDisableCode("");
      setMessage("MFA desabilitado.");
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Não foi possível desabilitar.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <header>
        <div>
          <p className="eyebrow">PROTEÇÃO DA CONTA</p>
          <h1>Segurança</h1>
          <p className="muted">
            Use um aplicativo autenticador para proteger o acesso ao painel.
          </p>
        </div>
      </header>
      {error && <p className="error">{error}</p>}
      {message && <p className="success notice">{message}</p>}
      <section className="security-grid">
        <article className="panel security-card">
          <h2>Habilitar autenticação em duas etapas</h2>
          <p className="muted">
            Compatível com Microsoft Authenticator, Google Authenticator,
            1Password e similares.
          </p>
          {setup ? (
            <form onSubmit={confirm}>
              <p>Adicione a conta usando a chave abaixo:</p>
              <code className="activation-key">{setup.secret}</code>
              <a className="authenticator-link" href={setup.otpAuthUri}>
                Abrir no aplicativo autenticador
              </a>
              <label>
                Código de 6 dígitos
                <input
                  className="totp-input"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  required
                />
              </label>
              <button disabled={busy}>Confirmar e habilitar</button>
            </form>
          ) : (
            <button disabled={busy} onClick={prepare}>
              Preparar MFA
            </button>
          )}
        </article>
        <article className="panel security-card">
          <h2>Desabilitar MFA</h2>
          <p className="muted">
            Por segurança, confirme novamente sua senha e o código atual.
          </p>
          <form onSubmit={disable}>
            <label>
              Senha
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <label>
              Código atual
              <input
                className="totp-input"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={disableCode}
                onChange={(e) =>
                  setDisableCode(e.target.value.replace(/\D/g, ""))
                }
                autoComplete="one-time-code"
                required
              />
            </label>
            <button className="danger" disabled={busy}>
              Desabilitar MFA
            </button>
          </form>
        </article>
      </section>
    </>
  );
}


// Rótulo honesto para o gráfico: a API devolve até 24 h de telemetria, mas o
// equipamento pode ter ficado offline; melhor dizer o período realmente plotado.
const telemetryWindow = (points: Array<{ capturedAt: string }>) => {
  if (points.length < 2) return "últimas 24h";
  const hours =
    (Date.parse(points[points.length - 1].capturedAt) - Date.parse(points[0].capturedAt)) / 3_600_000;
  if (!Number.isFinite(hours) || hours <= 0) return "últimas 24h";
  return hours >= 23.5 ? "últimas 24h" : `últimas ${Math.max(1, Math.round(hours))}h`;
};

function DevicePage({ id }: { id: string }) {
  // Suporte cadastra e diagnostica, mas não lê capturas, atividade, eventos ou relatórios.
  const currentUser = session.user();
  const canSeeContent = can(currentUser, "content.read");
  const [detail, setDetail] = useState<DeviceDetail>();
  const [events, setEvents] = useState<DeviceEvent[]>([]);
  const [eventLevel, setEventLevel] = useState("");
  const [eventCategory, setEventCategory] = useState("");
  const [eventFrom, setEventFrom] = useState(localInput(new Date(Date.now() - 7 * 86400000)));
  const [eventTo, setEventTo] = useState(localInput(new Date()));
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [eventsBusy, setEventsBusy] = useState(false);
  const [incidents, setIncidents] = useState<DeviceIncident[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tagDraft, setTagDraft] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [tagMessage, setTagMessage] = useState("");
  const [plans, setPlans] = useState<Plan[]>([]);
  const canOperate = can(currentUser, "device.state");
  const canRevoke = can(currentUser, "device.manage");
  const canManageTags = can(currentUser, "tag.manage");
  const canEditPolicy = can(currentUser, "policy.manage");
  const refresh = () =>
    api
      .device(id)
      .then(setDetail)
      .catch((e) => setError(e.message));
  const changeState = async (state: Device["lifecycleState"]) => {
    if (
      state === "revoked" &&
      !confirm(
        "Revogar desativa o agente deste equipamento (dá para reativar depois). Continuar?",
      )
    )
      return;
    setBusy(true);
    setError("");
    try {
      await api.updateDeviceState(id, state);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao alterar estado.");
    } finally {
      setBusy(false);
    }
  };
  const removeDevice = async () => {
    if (!confirm("Remover este equipamento do cadastro? O histórico e as capturas serão excluídos.")) return;
    setBusy(true);
    setError("");
    try {
      await api.deleteDevice(id);
      location.hash = "#/";
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao remover equipamento.");
      setBusy(false);
    }
  };
  const loadEvents = async (submit?: FormEvent) => {
    submit?.preventDefault();
    setEventsBusy(true);
    try {
      const page = await api.deviceEvents(id, {
        level: eventLevel || undefined,
        category: eventCategory || undefined,
        from: new Date(eventFrom).toISOString(),
        to: new Date(eventTo).toISOString(),
        limit: 100,
      });
      setEvents(page.items);
      setEventsLoaded(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao carregar eventos.");
    } finally {
      setEventsBusy(false);
    }
  };
  const addTag = () => {
    const value = tagInput.trim();
    if (!value) return;
    setTagMessage("");
    setTagDraft((current) =>
      current.some((existing) => existing.toLowerCase() === value.toLowerCase())
        ? current
        : [...current, value]);
    setTagInput("");
  };
  const saveTags = async () => {
    setTagMessage("");
    try {
      const result = await api.saveDeviceTags(id, tagDraft);
      setTagDraft(result.tags);
      setTagMessage("Etiquetas salvas.");
      refresh();
    } catch (e) {
      setTagMessage(e instanceof Error ? e.message : "Falha ao salvar etiquetas.");
    }
  };
  useEffect(() => {
    refresh();
    api.plans().then(setPlans).catch(() => undefined);
    if (!canSeeContent) return;
    const incidentTo = new Date();
    api
      .deviceIncidents(id, { from: new Date(incidentTo.getTime() - 7 * 86400000).toISOString(), to: incidentTo.toISOString() })
      .then(setIncidents)
      .catch(() => undefined);
  }, [id]);
  useEffect(() => {
    if (detail) setTagDraft(detail.device.tags ?? []);
  }, [detail]);
  if (error) return <p className="error">{error}</p>;
  if (!detail) return <p className="muted">Carregando…</p>;
  const d = detail.device;
  const companion = companionBadge(d);
  return (
    <>
      <a className="back" href="#/">
        ← Equipamentos
      </a>
      <PageHeader
        eyebrow="DETALHE DO EQUIPAMENTO"
        title={d.machineName}
        subtitle={`${d.osVersion} · Agente ${d.agentVersion}`}
        actions={
          <div className="device-controls">
          {canSeeContent && (
            <a className="button-link" href={`#/devices/${id}/timeline`}>
              Abrir timeline
            </a>
          )}
          <span className={`status ${d.status}`}>{d.status}</span>
          <span className={`status lifecycle-${d.lifecycleState}`}>
            {lifecycleLabel(d.lifecycleState)}
          </span>
          {d.status === "online" && detail.policy.screenshotsEnabled && companion && (
            <span className={`status ${companion.tone}`} title={companion.title}>
              {companion.label}
            </span>
          )}
          {canOperate && d.lifecycleState === "ready" && (
            <button
              disabled={busy}
              className="secondary"
              onClick={() => changeState("paused")}
            >
              Pausar
            </button>
          )}
          {((canOperate && d.lifecycleState === "paused") || (canRevoke && d.lifecycleState === "revoked")) && (
            <button disabled={busy} onClick={() => changeState("ready")}>
              Reativar
            </button>
          )}
          {canRevoke && d.lifecycleState !== "revoked" && (
            <button
              disabled={busy}
              className="danger"
              onClick={() => changeState("revoked")}
            >
              Revogar
            </button>
          )}
          {canRevoke && (
            <button disabled={busy} className="danger" onClick={removeDevice}>
              Remover do cadastro
            </button>
          )}
          </div>
        }
      />
      <section className="stats six">
        <Metric label="CPU" value={percent(d.cpuPercent)} />
        <Metric label="Memória" value={percent(d.memoryUsedPercent)} />
        <Metric label="Disco livre" value={bytes(d.diskFreeBytes)} />
        <Metric label="Uptime" value={duration(d.uptimeSeconds)} />
        <Metric label="Fila offline" value={`${d.bufferedSnapshotCount} · ${bytes(d.bufferedSnapshotBytes)}`} tone={d.bufferedSnapshotCount > 0 ? "orange" : "green"} />
        <Metric label="Conexão" value={`${d.status === "online" ? "Online" : "Offline"} · Proxy ${d.proxyMode}`} tone={d.status === "online" ? "green" : "orange"} />
      </section>
      <section className="panel plan-summary">
        <div>
          <p className="eyebrow">PLANO DO EQUIPAMENTO</p>
          <h2>{d.planName}</h2>
          <span className="muted">{d.screenRetentionDays} dias de telas · {d.operationalRetentionDays} dias de telemetria e eventos</span>
        </div>
        {session.user()?.platformRole === "platform_admin" && (
          <label>Alterar plano
            <select value={d.planId} disabled={busy} onChange={async (event) => {
              const selected = plans.find((plan) => plan.id === event.target.value);
              if (!selected || selected.id === d.planId) return;
              const downgrade = selected.screenRetentionDays < d.screenRetentionDays;
              if (downgrade && !confirm(`Ao mudar para ${selected.name}, capturas fora da nova retenção poderão ser excluídas no próximo ciclo. Continuar?`)) return;
              setBusy(true); setError("");
              try { await api.updateDevicePlan(id, selected.id); await refresh(); }
              catch (e) { setError(e instanceof Error ? e.message : "Falha ao alterar plano."); }
              finally { setBusy(false); }
            }}>
              {plans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name} — {plan.screenRetentionDays} dias</option>)}
            </select>
          </label>
        )}
      </section>
      <InventoryPanel deviceId={id} />
      {canSeeContent && incidents.length > 0 && (
        <section className="panel">
          <div className="panel-title">
            <h2>Incidentes</h2>
            <span>{incidents.length} nos últimos 7 dias</span>
          </div>
          <div className="event-list">
            {incidents.map((incident) => (
              <article key={incident.id} className={`event-row level-${incident.severity}`}>
                <div>
                  <strong>{incident.title}</strong>
                  <small>{incident.kind.replaceAll("_", " ")} · {incident.eventCount} evento(s) relacionado(s)</small>
                </div>
                <span className="event-time">{new Date(incident.startedAt).toLocaleString("pt-BR")}</span>
              </article>
            ))}
          </div>
        </section>
      )}
      <details
        hidden={!canSeeContent}
        className="panel collapsible"
        onToggle={(event) => {
          if (event.currentTarget.open && !eventsLoaded) void loadEvents();
        }}
      >
        <summary>
          <h2>Eventos do Windows</h2>
          <span>{eventsLoaded ? `${events.length} eventos` : ""}</span>
        </summary>
        <form className="event-filters" onSubmit={loadEvents}>
          <label>
            Nível
            <select value={eventLevel} onChange={(e) => setEventLevel(e.target.value)}>
              <option value="">Todos</option>
              <option value="critical">Crítico</option>
              <option value="error">Erro</option>
              <option value="warning">Aviso</option>
              <option value="information">Informação</option>
            </select>
          </label>
          <label>
            Categoria
            <select value={eventCategory} onChange={(e) => setEventCategory(e.target.value)}>
              <option value="">Todas</option>
              <option value="crash">Crash</option>
              <option value="bsod">Tela azul</option>
              <option value="power">Energia</option>
              <option value="service">Serviço</option>
              <option value="disk">Disco</option>
              <option value="hardware">Hardware</option>
              <option value="update">Update</option>
              <option value="security">Segurança</option>
              <option value="network">Rede</option>
              <option value="boot">Boot</option>
              <option value="performance">Desempenho</option>
            </select>
          </label>
          <label>
            De
            <input type="datetime-local" value={eventFrom} onChange={(e) => setEventFrom(e.target.value)} />
          </label>
          <label>
            Até
            <input type="datetime-local" value={eventTo} onChange={(e) => setEventTo(e.target.value)} />
          </label>
          <button disabled={eventsBusy}>{eventsBusy ? "Carregando…" : "Filtrar"}</button>
        </form>
        <div className="event-list">
          {events.map((event) => (
            <article key={event.id} className={`event-row level-${event.level}`}>
              <div>
                <strong>{event.title}</strong>
                <small>{event.subject ? `${event.subject} · ` : ""}{event.provider} · evento {event.eventId} · {event.channel}{event.source === "import" ? " · importado" : ""}</small>
                {event.message && <details><summary>Detalhes</summary><p>{event.message}</p></details>}
              </div>
              <span className="event-time">{new Date(event.occurredAt).toLocaleString("pt-BR")}</span>
            </article>
          ))}
          {eventsLoaded && !events.length && (
            <div className="empty">
              <b>Nenhum evento relevante</b>
              <span>Ajuste os filtros ou o período consultado.</span>
            </div>
          )}
        </div>
      </details>
      <section className="panel tag-panel">
        <div className="panel-title">
          <h2>Etiquetas</h2>
          <span>{tagDraft.length} de 20</span>
        </div>
        <div className="tag-list">
          {tagDraft.length
            ? tagDraft.map((item) => (
                <span className="tag-chip" key={item}>
                  {item}
                  {canManageTags && (
                    <button
                      className="tag-remove"
                      aria-label={`Remover ${item}`}
                      onClick={() => setTagDraft((current) => current.filter((tag) => tag !== item))}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))
            : <small>Sem etiquetas.</small>}
        </div>
        {canManageTags && (
          <div className="tag-editor">
            <input
              value={tagInput}
              maxLength={40}
              placeholder="Ex.: Marketing"
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addTag();
                }
              }}
            />
            <button className="secondary" onClick={addTag}>Adicionar</button>
            <button onClick={() => void saveTags()}>Salvar</button>
          </div>
        )}
        {tagMessage && <p className="muted">{tagMessage}</p>}
      </section>
      <section className="grid">
        <PolicyEditor deviceId={id} policy={detail.policy} onSaved={refresh} canEdit={canEditPolicy} />
        <article className="panel">
          <div className="panel-title">
            <h2>Atividade</h2>
            <span>
              {detail.telemetry.length} amostras · {telemetryWindow(detail.telemetry)}
            </span>
          </div>
          <TelemetryChart points={detail.telemetry} />
        </article>
      </section>
      {canSeeContent && <EventImportPanel deviceId={id} canImport={canRevoke} />}
    </>
  );
}


function PolicyEditor({
  deviceId,
  policy,
  onSaved,
  canEdit,
}: {
  deviceId: string;
  policy: Policy;
  onSaved: () => void;
  canEdit: boolean;
}) {
  const [draft, setDraft] = useState(policy);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState("");
  const update = (key: keyof Policy, value: number | boolean | string[]) =>
    setDraft((p) => ({ ...p, [key]: value }));
  const save = async () => {
    await api.updatePolicy(deviceId, draft);
    setEditing(false);
    setMessage("Política salva.");
    onSaved();
  };
  return (
    <article className="panel policy-panel">
      <div className="panel-title">
        <h2>Política aplicada</h2>
        {canEdit && (
          <button className="small-button" onClick={() => setEditing(!editing)}>
            {editing ? "Cancelar" : "Editar"}
          </button>
        )}
      </div>
      {editing && canEdit ? (
        <div className="policy-form">
          <label>
            Heartbeat (s)
            <input
              type="number"
              min="15"
              max="3600"
              value={draft.heartbeatSeconds}
              onChange={(e) =>
                update("heartbeatSeconds", Number(e.target.value))
              }
            />
          </label>
          <label>
            Telemetria (s)
            <input
              type="number"
              min="15"
              max="3600"
              value={draft.telemetrySeconds}
              onChange={(e) =>
                update("telemetrySeconds", Number(e.target.value))
              }
            />
          </label>
          <label>
            Captura periódica (s)
            <input
              type="number"
              min="30"
              max="86400"
              value={draft.screenshotSeconds}
              onChange={(e) =>
                update("screenshotSeconds", Number(e.target.value))
              }
            />
          </label>
          <label>
            Verificação visual (s)
            <input
              type="number"
              min="1"
              max="300"
              value={draft.visualCheckSeconds}
              onChange={(e) =>
                update("visualCheckSeconds", Number(e.target.value))
              }
            />
          </label>
          <label>
            Intervalo mínimo (s)
            <input
              type="number"
              min="1"
              max="300"
              value={draft.minimumCaptureIntervalSeconds}
              onChange={(e) =>
                update("minimumCaptureIntervalSeconds", Number(e.target.value))
              }
            />
          </label>
          <label>
            Alteração visual mínima (%)
            <input
              type="number"
              min="1"
              max="100"
              step="0.5"
              value={draft.visualChangeThresholdPercent}
              onChange={(e) =>
                update("visualChangeThresholdPercent", Number(e.target.value))
              }
            />
          </label>
          <label>
            Atraso dos gatilhos (ms)
            <input
              type="number"
              min="0"
              max="10000"
              value={draft.triggerDelayMilliseconds}
              onChange={(e) =>
                update("triggerDelayMilliseconds", Number(e.target.value))
              }
            />
          </label>
          <label>
            Qualidade JPEG
            <input
              type="number"
              min="35"
              max="95"
              value={draft.jpegQuality}
              onChange={(e) => update("jpegQuality", Number(e.target.value))}
            />
          </label>
          <label>
            Debounce da atividade (ms)
            <input type="number" min="100" max="10000" value={draft.activityDebounceMilliseconds}
              onChange={(e) => update("activityDebounceMilliseconds", Number(e.target.value))} />
          </label>
          <label>
            Inventário (segundos)
            <input type="number" min="3600" max="604800" step="3600" value={draft.inventorySeconds ?? 86400}
              onChange={(e) => update("inventorySeconds", Number(e.target.value))} />
          </label>
          <label>
            Processos protegidos (separados por vírgula)
            <input
              value={draft.excludedProcesses.join(", ")}
              onChange={(e) =>
                update(
                  "excludedProcesses",
                  e.target.value
                    .split(",")
                    .map((value) => value.trim())
                    .filter(Boolean),
                )
              }
            />
          </label>
          <div className="policy-checks">
            <label className="check">
              <input type="checkbox" checked={draft.activityTrackingEnabled}
                onChange={(e) => update("activityTrackingEnabled", e.target.checked)} />{" "}
              Metadados de atividade
            </label>
            <label className="check">
              <input type="checkbox" checked={draft.windowTitlesEnabled}
                onChange={(e) => update("windowTitlesEnabled", e.target.checked)} />{" "}
              Títulos de janela (sensível)
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={draft.screenshotsEnabled}
                onChange={(e) => update("screenshotsEnabled", e.target.checked)}
              />{" "}
              Capturas habilitadas
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={draft.captureOnClick}
                onChange={(e) => update("captureOnClick", e.target.checked)}
              />{" "}
              Após clique
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={draft.captureOnEnter}
                onChange={(e) => update("captureOnEnter", e.target.checked)}
              />{" "}
              Após Enter
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={draft.captureOnWindowChange}
                onChange={(e) =>
                  update("captureOnWindowChange", e.target.checked)
                }
              />{" "}
              Ao trocar janela
            </label>
          </div>
          <button onClick={save}>Salvar política</button>
        </div>
      ) : (
        <dl>
          <div>
            <dt>Metadados de atividade</dt>
            <dd>{policy.activityTrackingEnabled ? `Ativos · ${policy.windowTitlesEnabled ? "com títulos" : "sem títulos"}` : "Desativados"}</dd>
          </div>
          <div>
            <dt>Captura periódica</dt>
            <dd>
              {policy.screenshotsEnabled
                ? `${policy.screenshotSeconds}s`
                : "Desativada"}
            </dd>
          </div>
          <div>
            <dt>Detecção visual</dt>
            <dd>
              {policy.visualCheckSeconds}s ·{" "}
              {policy.visualChangeThresholdPercent}%
            </dd>
          </div>
          <div>
            <dt>Gatilhos</dt>
            <dd>
              {[
                policy.captureOnClick && "clique",
                policy.captureOnEnter && "Enter",
                policy.captureOnWindowChange && "janela",
              ]
                .filter(Boolean)
                .join(", ") || "Nenhum"}
            </dd>
          </div>
          <div>
            <dt>Intervalo mínimo</dt>
            <dd>{policy.minimumCaptureIntervalSeconds}s</dd>
          </div>
          <div>
            <dt>Qualidade</dt>
            <dd>{policy.jpegQuality}%</dd>
          </div>
          <div>
            <dt>Retenção efetiva do plano</dt>
            <dd>{policy.snapshotRetentionDays} dias</dd>
          </div>
          {message && <p className="success">{message}</p>}
        </dl>
      )}
    </article>
  );
}

function UsersPage() {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [passwordUser, setPasswordUser] = useState<ManagedUser>();
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const currentUser = session.user();
  // Revenda é cadastro de plataforma: só o Master fora de escopo cria esse tipo de
  // usuário, e o cadastro precisa apontar qual revenda ele representa.
  const canManagePartners =
    currentUser?.platformRole === "platform_admin" &&
    !isScopedSession(session.token(), currentUser) &&
    can(currentUser, "user.manage");
  const [partners, setPartners] = useState<Partner[]>([]);
  // Empresas adicionais: concessão vale fora do tenant do token, então só o Master
  // sem escopo atribui (o endpoint devolve 403 para os demais).
  const canAssignCompanies = canManagePartners;
  const [companyOptions, setCompanyOptions] = useState<Tenant[]>([]);
  const [companiesUser, setCompaniesUser] = useState<ManagedUser>();
  const pagedUsers = usePagedList(users);
  const refresh = () =>
    api
      .users()
      .then(setUsers)
      .catch((e) => setError(e.message));
  useEffect(() => {
    void refresh();
  }, []);
  useEffect(() => {
    if (canManagePartners) api.partners().then(setPartners).catch(() => undefined);
  }, [canManagePartners]);
  useEffect(() => {
    if (canAssignCompanies) api.tenants().then(setCompanyOptions).catch(() => undefined);
  }, [canAssignCompanies]);
  const toggle = async (user: ManagedUser) => {
    setError("");
    try {
      await api.updateUser({ ...user, isActive: !user.isActive });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao atualizar usuário.");
    }
  };
  const resetMfa = async (user: ManagedUser) => {
    if (!confirm(`Remover o MFA de ${user.email}?`)) return;
    try {
      await api.resetUserMfa(user.id);
      setMessage("MFA removido e evento registrado na auditoria.");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao remover MFA.");
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="GESTÃO COMERCIAL"
        title="Usuários"
        subtitle="Controle quem acessa cada empresa e o nível de permissão."
        actions={<button onClick={() => setShowAdd(true)}>+ Novo usuário</button>}
      />
      <section className="panel">
        <div className="panel-title">
          <h2>Níveis de acesso</h2>
        </div>
        <ul className="role-legend">
          <li><b>ADM da empresa</b> — controle total da empresa e dos usuários.</li>
          <li><b>Administrador</b> — opera equipamentos, políticas, chaves e auditoria; não gerencia usuários.</li>
          <li><b>User</b> — consulta equipamentos, timeline, eventos e relatórios; não altera nada.</li>
          <li><b>Suporte</b> — cadastra e diagnostica equipamentos, sem acesso a capturas, atividade ou relatórios.</li>
          {partners.length > 0 && <li><b>Revenda — ADM / Suporte</b> — equipe da revenda na carteira de empresas dela; o ADM vê conteúdo e auditoria, o Suporte não.</li>}
        </ul>
        <p className="muted">
          O suporte Guardian Insight acessa uma empresa temporariamente, com leitura e pausa/reativação de
          equipamentos, mediante concessão auditada. Revogar equipamento e editar política são
          exclusivos de proprietário e administrador.
        </p>
      </section>
      {error && <p className="error">{error}</p>}
      {message && <p className="success notice">{message}</p>}
      <section className="panel">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Usuário</th>
                <th>Nível</th>
                {partners.length > 0 && <th>Revenda</th>}
                <th>MFA</th>
                <th>Estado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {pagedUsers.pageItems.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.displayName}</strong>
                    <small>{user.email}</small>
                  </td>
                  <td>
                    <span className={`role role-${user.role}`}>
                      {roleLabel(user.role)}
                    </span>
                  </td>
                  {partners.length > 0 && (
                    <td>{partners.find((partner) => partner.id === user.partnerId)?.name ?? "—"}</td>
                  )}
                  <td>{user.mfaEnabled ? "Ativo" : "Não configurado"}</td>
                  <td>{user.isActive ? "Ativo" : "Desativado"}</td>
                  <td>
                    <div className="row-actions">
                      <button
                        className="small-button"
                        onClick={() => toggle(user)}
                      >
                        {user.isActive ? "Desativar" : "Ativar"}
                      </button>
                      <button
                        className="small-button"
                        onClick={() => setPasswordUser(user)}
                      >
                        Nova senha
                      </button>
                      {user.mfaEnabled && (
                        <button
                          className="small-button danger"
                          onClick={() => resetMfa(user)}
                        >
                          Remover MFA
                        </button>
                      )}
                      {canAssignCompanies && !user.role.startsWith("partner_") && (
                        <button
                          className="small-button"
                          onClick={() => setCompaniesUser(user)}
                        >
                          Empresas
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ListControls page={pagedUsers.page} pages={pagedUsers.pages} size={pagedUsers.size} total={pagedUsers.total} onSize={pagedUsers.changeSize} onPrev={pagedUsers.prev} onNext={pagedUsers.next} />
      </section>
      {companiesUser && (
        <UserCompaniesModal
          user={companiesUser}
          companies={companyOptions}
          onClose={() => setCompaniesUser(undefined)}
          onSaved={() => {
            setCompaniesUser(undefined);
            setMessage("Empresas do usuário atualizadas e auditadas.");
            void refresh();
          }}
        />
      )}
      {showAdd && (
        <AddUserModal
          partners={partners}
          companies={companyOptions}
          canAssignOwner={currentUser?.platformRole === "platform_admin" || currentUser?.role === "owner" || currentUser?.role === "admin"}
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            refresh();
          }}
        />
      )}
      {passwordUser && (
        <ResetPasswordModal
          user={passwordUser}
          onClose={() => setPasswordUser(undefined)}
          onSaved={() => {
            setPasswordUser(undefined);
            setMessage("Senha atualizada e evento registrado na auditoria.");
          }}
        />
      )}
    </>
  );
}

const companyRoleLabel = (role: string) =>
  role === "owner" ? "ADM da empresa" : role === "admin" ? "Administrador" : role === "viewer" ? "Consulta" : role;

function UserCompaniesModal({
  user,
  companies,
  onClose,
  onSaved,
}: {
  user: ManagedUser;
  companies: Tenant[];
  onClose: () => void;
  onSaved: () => void;
}) {
  // Papel por empresa; a empresa de origem mantém o nível do usuário e é ignorada na gravação.
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [home, setHome] = useState<UserCompanyRole>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .userCompanies(user.id)
      .then((page) => {
        // A origem é referência fixa: o nível vem do cadastro e não é gravado aqui.
        setHome(page.items.find((item) => item.isHome));
        setSelected(
          Object.fromEntries(
            page.items.filter((item) => !item.isHome).map((item) => [item.tenantId, item.role]),
          ),
        );
      })
      .catch((e) =>
        setError(e instanceof Error ? e.message : "Falha ao carregar empresas."),
      );
  }, [user.id]);
  const toggle = (tenantId: string) =>
    setSelected((current) => {
      if (current[tenantId]) {
        const next = { ...current };
        delete next[tenantId];
        return next;
      }
      return { ...current, [tenantId]: user.role };
    });
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      // A fonte é o que está marcado, não a lista de empresas da tela: se ela estiver
      // desatualizada, enviar só `companies` apagaria a concessão ausente (o backend
      // substitui todas as concessões fora da empresa de origem).
      await api.setUserCompanies(
        user.id,
        Object.entries(selected).map(([tenantId, role]) => ({ tenantId, role })),
      );
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao salvar empresas.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <section className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <button className="close" onClick={onClose}>
          ×
        </button>
        <p className="eyebrow">MULTIEMPRESA</p>
        <h2>Empresas de {user.displayName}</h2>
        <p className="muted">
          A empresa de origem mantém o nível do cadastro; as marcadas aqui são
          adicionais, cada uma com o próprio nível. Só o Master sem escopo altera.
        </p>
        {error && <p className="error">{error}</p>}
        {!home && !error && <p className="muted">Carregando a empresa de origem…</p>}
        {!home && error && (
          <p className="muted">Sem a empresa de origem não é seguro salvar; feche e abra novamente.</p>
        )}
        <div className="company-checks">
          {home && (
            <div className="company-check company-home">
              <span>
                {companies.find((company) => company.id === home.tenantId)?.name ?? "Empresa de origem"}
                <small>Empresa de origem · nível do cadastro</small>
              </span>
              <strong>{companyRoleLabel(home.role)}</strong>
            </div>
          )}
          {companies
            .filter((company) => company.id !== home?.tenantId)
            .map((company) => (
            <div className="company-check" key={company.id}>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(selected[company.id])}
                  onChange={() => toggle(company.id)}
                />
                {company.name}
              </label>
              {selected[company.id] && (
                <select
                  value={selected[company.id]}
                  onChange={(e) =>
                    setSelected((current) => ({
                      ...current,
                      [company.id]: e.target.value,
                    }))
                  }
                >
                  <option value="owner">ADM da empresa</option>
                  <option value="admin">Administrador</option>
                  <option value="viewer">Consulta</option>
                </select>
              )}
            </div>
          ))}
        </div>
        <div className="row-actions">
          <button className="small-button" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button onClick={() => void save()} disabled={busy || !home}>
            {busy ? "Salvando…" : "Salvar empresas"}
          </button>
        </div>
      </section>
    </div>
  );
}

function AddUserModal({
  partners,
  companies,
  canAssignOwner,
  onClose,
  onCreated,
}: {
  partners: Partner[];
  companies: Tenant[];
  canAssignOwner: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<ManagedUser["role"]>("viewer");
  const [partnerId, setPartnerId] = useState("");
  const [password, setPassword] = useState("");
  const [companyIds, setCompanyIds] = useState<string[]>([]);
  const partnerRole = role === "partner_admin" || role === "partner_support";
  // A empresa de origem já vem do cadastro; aqui só as adicionais, com o mesmo papel.
  const canGrantCompanies = !partnerRole && companies.length > 0;
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const created = await api.createUser({
        email,
        displayName,
        role,
        password,
        partnerId: partnerRole ? partnerId : undefined,
      });
      if (canGrantCompanies && companyIds.length > 0)
        await api.setUserCompanies(created.id, companyIds.map((tenantId) => ({ tenantId, role })));
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao criar usuário.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <section className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <button className="close" onClick={onClose}>
          ×
        </button>
        <p className="eyebrow">NOVO ACESSO</p>
        <h2>Adicionar usuário</h2>
        <form onSubmit={submit}>
          <label>
            Nome
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              minLength={2}
              maxLength={160}
              required
            />
          </label>
          <label>
            E-mail
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label>
            Nível de acesso
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as ManagedUser["role"])}
            >
              <option value="viewer">User</option>
              <option value="admin">Administrador</option>
              {canAssignOwner && <option value="owner">ADM da empresa</option>}
              {partners.length > 0 && <option value="partner_support">Revenda — Suporte</option>}
              {partners.length > 0 && <option value="partner_admin">Revenda — ADM</option>}
            </select>
          </label>
          {partnerRole && (
            <label>
              Revenda
              <select
                value={partnerId}
                onChange={(e) => setPartnerId(e.target.value)}
                required
              >
                <option value="">Selecione a revenda</option>
                {partners.filter((partner) => partner.isActive).map((partner) => (
                  <option key={partner.id} value={partner.id}>{partner.name}</option>
                ))}
              </select>
            </label>
          )}
          <label>
            Senha inicial
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={12}
              maxLength={256}
              autoComplete="new-password"
              required
            />
          </label>
          {canGrantCompanies && (
            <fieldset className="company-checks">
              <legend>Empresas adicionais</legend>
              <small>Além da empresa atual, com o mesmo nível de acesso.</small>
              {companies.map((company) => (
                <label key={company.id} className="company-check">
                  <input
                    type="checkbox"
                    checked={companyIds.includes(company.id)}
                    onChange={(event) =>
                      setCompanyIds((current) =>
                        event.target.checked
                          ? [...current, company.id]
                          : current.filter((id) => id !== company.id),
                      )
                    }
                  />
                  {company.name}
                </label>
              ))}
            </fieldset>
          )}
          <div className="role-help">
            <b>{roleLabel(role)}</b>
            <span>
                {role === "viewer"
                  ? "Consulta equipamentos e timeline, sem alterações."
                  : role === "partner_support"
                    ? "Atende a carteira da revenda sem ver capturas, atividade ou auditoria."
                    : role === "partner_admin"
                      ? "Administra a carteira da revenda, com acesso ao conteúdo das empresas."
                      : role === "admin"
                        ? "Opera equipamentos e políticas, sem gestão de usuários."
                        : "Controle completo da empresa e dos usuários."}
            </span>
          </div>
          {error && <p className="error">{error}</p>}
          <button disabled={busy}>{busy ? "Criando…" : "Criar usuário"}</button>
        </form>
      </section>
    </div>
  );
}

function ResetPasswordModal({
  user,
  onClose,
  onSaved,
}: {
  user: ManagedUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirmation) {
      setError("As senhas não coincidem.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.resetUserPassword(user.id, password);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao alterar senha.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <section className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <button className="close" onClick={onClose}>
          ×
        </button>
        <p className="eyebrow">CREDENCIAL</p>
        <h2>Nova senha para {user.displayName}</h2>
        <form onSubmit={submit}>
          <label>
            Nova senha
            <input
              type="password"
              minLength={12}
              maxLength={256}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>
          <label>
            Confirmar senha
            <input
              type="password"
              minLength={12}
              maxLength={256}
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>
          {error && <p className="error">{error}</p>}
          <button disabled={busy}>
            {busy ? "Salvando…" : "Atualizar senha"}
          </button>
        </form>
      </section>
    </div>
  );
}

function InventoryPanel({ deviceId }: { deviceId: string }) {
  const [inventory, setInventory] = useState<DeviceInventory | null>(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  useEffect(() => {
    let active = true;
    api
      .inventory(deviceId)
      .then((value) => {
        if (!active) return;
        setInventory(value);
        setError("");
      })
      .catch((e) => {
        if (active) setError(e instanceof Error ? e.message : "Falha ao carregar o inventário.");
      });
    return () => {
      active = false;
    };
  }, [deviceId]);

  if (error) {
    return (
      <section className="panel">
        <p className="error">{error}</p>
      </section>
    );
  }
  if (!inventory) {
    return (
      <section className="panel">
        <div className="panel-title">
          <h2>Inventário</h2>
        </div>
        <div className="empty">
          <b>Aguardando a primeira coleta</b>
          <span>O agente envia o inventário ao iniciar o serviço.</span>
        </div>
      </section>
    );
  }

  const data = inventory.inventory;
  const hardware = data.hardware;
  const os = data.os;
  const needle = filter.trim().toLowerCase();
  const software = needle
    ? data.software.filter((item) => `${item.name} ${item.publisher ?? ""} ${item.version ?? ""}`.toLowerCase().includes(needle))
    : data.software;
  const services = needle
    ? data.services.filter((item) => `${item.name} ${item.displayName}`.toLowerCase().includes(needle))
    : data.services;
  return (
    <section className="panel">
      <div className="panel-title">
        <h2>Inventário</h2>
        <span>coletado em {new Date(inventory.collectedAt).toLocaleString("pt-BR")}</span>
      </div>
      <div className="inventory-summary">
        <div>
          <small>Fabricante / modelo</small>
          <strong>{[hardware.manufacturer, hardware.model].filter(Boolean).join(" ") || "—"}</strong>
        </div>
        <div>
          <small>Sistema</small>
          <strong>{[os.productName, os.displayVersion, os.build].filter(Boolean).join(" ") || "—"}</strong>
        </div>
        <div>
          <small>CPU</small>
          <strong>{hardware.cpu ?? "—"}{hardware.cpuLogical ? ` · ${hardware.cpuLogical} threads` : ""}</strong>
        </div>
        <div>
          <small>Memória</small>
          <strong>{bytes(hardware.ramBytes)}</strong>
        </div>
        <div>
          <small>BIOS / placa</small>
          <strong>
            {[hardware.biosVendor, hardware.biosVersion].filter(Boolean).join(" ") || "—"}
            {hardware.boardProduct ? ` · ${hardware.boardProduct}` : ""}
          </strong>
        </div>
        <div>
          <small>Último boot (aprox.)</small>
          <strong>{os.lastBootAt ? new Date(os.lastBootAt).toLocaleString("pt-BR") : "—"}</strong>
        </div>
      </div>
      <label className="inventory-filter">
        Filtrar programas e serviços
        <input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="ex.: Office, Spooler" />
      </label>
      <details className="inventory-section" open>
        <summary>Programas instalados <b>{data.software.length}</b></summary>
        <div className="inventory-list">
          {software.slice(0, 300).map((item) => (
            <div className="inventory-row" key={`${item.name}|${item.version ?? ""}`}>
              <span>{item.name}</span>
              <span>{[item.version, item.publisher].filter(Boolean).join(" · ")}</span>
            </div>
          ))}
          {!software.length && <div className="muted">Nenhum programa encontrado.</div>}
        </div>
      </details>
      <details className="inventory-section">
        <summary>Serviços <b>{data.services.length}</b></summary>
        <div className="inventory-list">
          {services.slice(0, 300).map((item) => (
            <div className="inventory-row" key={item.name}>
              <span>{item.displayName}<small>{item.name}</small></span>
              <span>{item.status} · {item.startMode}</span>
            </div>
          ))}
          {!services.length && <div className="muted">Nenhum serviço encontrado.</div>}
        </div>
      </details>
      <details className="inventory-section">
        <summary>Discos <b>{data.disks.length}</b></summary>
        <div className="inventory-list">
          {data.disks.map((disk) => (
            <div className="inventory-row" key={disk.name}>
              <span>{disk.name} <small>{disk.fileSystem}</small></span>
              <span>{bytes(disk.freeBytes)} livres de {bytes(disk.totalBytes)}</span>
            </div>
          ))}
          {!data.disks.length && <div className="muted">Nenhum disco encontrado.</div>}
        </div>
      </details>
      <details className="inventory-section">
        <summary>Vídeo e rede <b>{data.gpus.length + data.networks.length}</b></summary>
        <div className="inventory-list">
          {data.gpus.map((gpu) => (
            <div className="inventory-row" key={`gpu-${gpu.name}`}>
              <span>{gpu.name}</span>
              <span>{gpu.driverVersion ? `driver ${gpu.driverVersion}` : ""}</span>
            </div>
          ))}
          {data.networks.map((nic) => (
            <div className="inventory-row" key={`nic-${nic.name}`}>
              <span>{nic.description ?? nic.name}<small>{nic.name}</small></span>
              <span>{nic.macAddress ?? ""}{nic.speedBitsPerSecond ? ` · ${Math.round(nic.speedBitsPerSecond / 1_000_000)} Mbps` : ""}</span>
            </div>
          ))}
        </div>
      </details>
      <details className="inventory-section">
        <summary>Atualizações instaladas <b>{data.hotfixes.length}</b></summary>
        <div className="inventory-chips">
          {data.hotfixes.map((hotfix) => <span key={hotfix}>{hotfix}</span>)}
          {!data.hotfixes.length && <div className="muted">Nenhuma atualização encontrada.</div>}
        </div>
      </details>
    </section>
  );
}

function EventImportPanel({ deviceId, canImport }: { deviceId: string; canImport: boolean }) {
  const [imports, setImports] = useState<EventImport[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const load = () => api.eventImports(deviceId).then(setImports).catch(() => undefined);
  useEffect(() => {
    void load();
  }, [deviceId]);
  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await api.uploadEventImport(deviceId, file);
      setMessage(`"${file.name}" enviado. O agente processa na próxima verificação (até 1 min, se online).`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao importar o arquivo.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel">
      <div className="panel-title">
        <h2>Importar log (.evtx)</h2>
        <span>processado pelo agente</span>
      </div>
      <p className="muted">
        Envie um arquivo .evtx exportado do Visualizador de Eventos. O agente lê o arquivo no
        Windows e envia os eventos para este equipamento.
      </p>
      {canImport && (
        <label className="import-upload">
          <input type="file" accept=".evtx" disabled={busy} onChange={upload} />
          <span>{busy ? "Enviando…" : "Selecionar .evtx"}</span>
        </label>
      )}
      {message && <p className="muted">{message}</p>}
      {error && <p className="error">{error}</p>}
      {imports.length > 0 && (
        <div className="inventory-list">
          {imports.map((item) => (
            <div className="inventory-row" key={item.id}>
              <span>
                {item.fileName}
                <small>
                  {new Date(item.requestedAt).toLocaleString("pt-BR")} · {bytes(item.contentLength)}
                </small>
              </span>
              <span>{importStatus(item)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function importStatus(item: EventImport) {
  switch (item.status) {
    case "pending": return "aguardando agente";
    case "processing": return "processando…";
    case "completed": return `${item.importedCount ?? 0} eventos${item.failedCount ? ` · ${item.failedCount} falhas` : ""}`;
    default: return `falhou${item.error ? `: ${item.error}` : ""}`;
  }
}

function ReportsPage() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [report, setReport] = useState<EventReportSummary | null>(null);
  const [deviceId, setDeviceId] = useState("");
  const [from, setFrom] = useState(localInput(new Date(Date.now() - 7 * 86400000)));
  const [to, setTo] = useState(localInput(new Date()));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const params = () => ({
    deviceId: deviceId || undefined,
    from: new Date(from).toISOString(),
    to: new Date(to).toISOString(),
  });
  const load = async (event?: FormEvent) => {
    event?.preventDefault();
    setBusy(true);
    setError("");
    try {
      setReport(await api.eventReport(params()));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao carregar o relatório.");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    api.devices().then(setDevices).catch(() => undefined);
    void load();
  }, []);
  const exportCsv = async () => {
    setBusy(true);
    setError("");
    try {
      const blob = await api.exportEventsCsv(params());
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `eventos-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      // Revogar no próximo tick evita interromper o download em navegadores que
      // ainda estão consumindo o blob (Safari/Firefox).
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao exportar os eventos.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <header>
        <div>
          <p className="eyebrow">ANÁLISE</p>
          <h1>Relatórios</h1>
          <p className="muted">Resumo dos eventos do Windows por período e equipamento.</p>
        </div>
      </header>
      <form className="panel activity-search" onSubmit={load}>
        <label>Equipamento<select value={deviceId} onChange={(event) => setDeviceId(event.target.value)}><option value="">Todos</option>{devices.map((device) => <option key={device.id} value={device.id}>{device.machineName}</option>)}</select></label>
        <label>De<input type="datetime-local" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
        <label>Até<input type="datetime-local" value={to} onChange={(event) => setTo(event.target.value)} /></label>
        <button disabled={busy}>{busy ? "Carregando…" : "Atualizar"}</button>
        <button type="button" className="secondary" disabled={busy} onClick={exportCsv}>Exportar CSV</button>
      </form>
      {error && <p className="error">{error}</p>}
      {report && (
        <>
          <section className="stats">
            <Metric label="Eventos" value={report.total.toString()} />
            <Metric label="Equipamentos" value={report.topDevices.length.toString()} />
            <Metric label="Período" value={`${new Date(report.from).toLocaleDateString("pt-BR")} – ${new Date(report.to).toLocaleDateString("pt-BR")}`} />
          </section>
          <section className="grid">
            <ReportTable title="Por nível" items={report.byLevel} />
            <ReportTable title="Por categoria" items={report.byCategory} />
            <ReportTable title="Equipamentos com mais eventos" items={report.topDevices.map((item) => ({ key: item.machineName, count: item.count }))} />
            <ReportTable title="Assuntos mais frequentes" items={report.topSubjects} />
          </section>
        </>
      )}
    </>
  );
}

function ReportTable({ title, items }: { title: string; items: Array<{ key: string; count: number }> }) {
  return (
    <article className="panel">
      <div className="panel-title">
        <h2>{title}</h2>
        <span>{items.length}</span>
      </div>
      <div className="inventory-list">
        {items.map((item) => (
          <div className="inventory-row" key={item.key}>
            <span>{item.key}</span>
            <span>{item.count}</span>
          </div>
        ))}
        {!items.length && (
          <div className="empty">
            <b>Sem dados</b>
            <span>Nenhum evento no período.</span>
          </div>
        )}
      </div>
    </article>
  );
}

function EventsPage() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [events, setEvents] = useState<TenantEvent[]>([]);
  const [deviceId, setDeviceId] = useState("");
  const [level, setLevel] = useState("");
  const [category, setCategory] = useState("");
  const pagedEvents = usePagedList(events);
  const [from, setFrom] = useState(localInput(new Date(Date.now() - 7 * 86400000)));
  const [to, setTo] = useState(localInput(new Date()));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async (event?: FormEvent) => {
    event?.preventDefault();
    setBusy(true);
    setError("");
    try {
      const page = await api.events({
        deviceId: deviceId || undefined,
        level: level || undefined,
        category: category || undefined,
        from: new Date(from).toISOString(),
        to: new Date(to).toISOString(),
        limit: 200,
      });
      setEvents(page.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao carregar eventos.");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    api.devices().then(setDevices).catch(() => undefined);
    void load();
  }, []);
  return (
    <>
      <PageHeader
        eyebrow="LOGS DO WINDOWS"
        title="Eventos do Windows"
        subtitle="Eventos de estabilidade e segurança coletados dos equipamentos."
      />
      <FilterBar onSubmit={load} actions={<button disabled={busy}>{busy ? "Carregando…" : "Filtrar"}</button>}>
        <label>Equipamento<select value={deviceId} onChange={(e) => setDeviceId(e.target.value)}><option value="">Todos</option>{devices.map((d) => <option key={d.id} value={d.id}>{d.machineName}</option>)}</select></label>
        <label>Nível<select value={level} onChange={(e) => setLevel(e.target.value)}><option value="">Todos</option><option value="critical">Crítico</option><option value="error">Erro</option><option value="warning">Aviso</option><option value="information">Informação</option></select></label>
        <label>Categoria<select value={category} onChange={(e) => setCategory(e.target.value)}><option value="">Todas</option><option value="crash">Crash</option><option value="bsod">Tela azul</option><option value="power">Energia</option><option value="service">Serviço</option><option value="disk">Disco</option><option value="hardware">Hardware</option><option value="update">Update</option><option value="security">Segurança</option><option value="network">Rede</option><option value="boot">Boot</option><option value="performance">Desempenho</option></select></label>
        <label>De<input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label>Até<input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} /></label>
      </FilterBar>
      {error && <p className="error">{error}</p>}
      <section className="panel">
        <div className="panel-title">
          <h2>Eventos encontrados</h2>
          <span>{events.length}</span>
        </div>
        <div className="event-list">
          {pagedEvents.pageItems.map((event) => (
            <article key={event.id} className={`event-row level-${event.level}`}>
              <div>
                <strong>{event.title}</strong>
                <small>{event.machineName} · {event.subject ? `${event.subject} · ` : ""}{event.provider} · evento {event.eventId} · {event.channel}{event.source === "import" ? " · importado" : ""}</small>
                {event.message && <details><summary>Detalhes</summary><p>{event.message}</p></details>}
              </div>
              <span className="event-time">{new Date(event.occurredAt).toLocaleString("pt-BR")}</span>
            </article>
          ))}
          {!events.length && (
            <div className="empty">
              <b>Nenhum evento</b>
              <span>Ajuste os filtros ou aguarde a coleta dos equipamentos.</span>
            </div>
          )}
        </div>
        <ListControls page={pagedEvents.page} pages={pagedEvents.pages} size={pagedEvents.size} total={pagedEvents.total} onSize={pagedEvents.changeSize} onPrev={pagedEvents.prev} onNext={pagedEvents.next} />
      </section>
    </>
  );
}

function PoliciesPage() {
  const [devices, setDevices] = useState<Device[]>([]);
  useEffect(() => {
    api.devices().then(setDevices);
  }, []);
  return (
    <>
      <header>
        <div>
          <p className="eyebrow">CONFIGURAÇÃO</p>
          <h1>Políticas</h1>
          <p className="muted">
            Selecione um equipamento para ajustar heartbeat e comportamento das capturas.
          </p>
        </div>
      </header>
      <section className="panel list-cards">
        {devices.map((d) => (
          <a href={`#/devices/${d.id}`} key={d.id}>
            <div>
              <strong>{d.machineName}</strong>
              <small>{d.osVersion}</small>
            </div>
            <span>Configurar →</span>
          </a>
        ))}
        {!devices.length && (
          <div className="empty">
            <b>Nenhum equipamento</b>
            <span>Ative um agente para configurar sua política.</span>
          </div>
        )}
      </section>
    </>
  );
}

